import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'GST Invoice Format Requirements India (2025)',
      'description': 'Mandatory fields, format rules, e-invoicing thresholds, and the difference between a tax invoice and a bill of supply under Indian GST law.',
      'url': 'https://www.invilic.com/blog/gst-invoice-format-india',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'Is there a specific GST invoice template prescribed by the government?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'No, the GST law does not prescribe a specific template or format. The CGST Rules 2017 specify mandatory fields that must be present, but the layout, design, and appearance are entirely up to the supplier. You can use any invoice software or template as long as all mandatory fields are included.' },
        },
        {
          '@type': 'Question',
          'name': 'What is e-invoicing under GST and who needs it?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'E-invoicing under GST (from GSTN) requires certain businesses to generate invoices through the Invoice Registration Portal (IRP), which assigns a unique Invoice Reference Number (IRN) and QR code. As of 2024, e-invoicing is mandatory for businesses with aggregate annual turnover exceeding ₹5 crore. The IRP validates the invoice data and returns an IRN within seconds.' },
        },
        {
          '@type': 'Question',
          'name': 'How many copies of a GST invoice are required?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'For supply of goods, three copies are required: Original (for buyer), Duplicate (for transporter), and Triplicate (for supplier). For supply of services, two copies are sufficient: Original (for recipient) and Duplicate (for supplier). With e-invoicing and digital records, physical copies are often not required but the invoice must be retrievable electronically.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the penalty for not issuing a GST invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Under Section 122 of the CGST Act, failure to issue a tax invoice attracts a penalty equal to the tax involved or ₹10,000, whichever is higher. Repeated violations can attract a penalty of ₹25,000. Additionally, if GST is collected but not remitted, the penalty is 100% of the tax evaded.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const mandatoryFields = [
  { field: 'Name, address, GSTIN of supplier', rule: 'Your registered GSTIN must match your GSTN certificate' },
  { field: 'Nature of supply', rule: 'Taxable, exempt, nil-rated, or zero-rated' },
  { field: 'Whether the supply is intra-state or inter-state', rule: 'Determines CGST+SGST vs IGST' },
  { field: 'Consecutive serial number', rule: 'Unique per financial year, up to 16 characters, may include letters, numbers, /, - and no other characters' },
  { field: 'Date of issue', rule: 'DD/MM/YYYY format is standard' },
  { field: 'Name, address, GSTIN of recipient (if registered)', rule: 'For unregistered buyers, GSTIN may be omitted' },
  { field: 'HSN code (goods) or SAC code (services)', rule: '4-digit for turnover ≤ ₹5 crore; 6-digit for > ₹5 crore' },
  { field: 'Quantity and unit (for goods)', rule: 'In the unit of measurement relevant to the goods' },
  { field: 'Total taxable value', rule: 'After deducting any discount' },
  { field: 'Rate and amount of applicable tax', rule: 'CGST + SGST (intra-state) or IGST (inter-state) shown separately' },
  { field: 'Place of supply (state name and code)', rule: 'Determines which state receives the tax' },
  { field: 'Reverse charge indicator', rule: 'Y/N — whether tax is payable by the recipient under reverse charge' },
  { field: 'Signature or digital signature of supplier', rule: 'Or authorised representative' },
];

export default function GstInvoiceFormatPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="GST Invoice Format Requirements India (2025) | Invilic Blog"
        description="Mandatory fields for a GST tax invoice in India — format rules, HSN/SAC codes, e-invoicing thresholds, and bill of supply vs tax invoice explained."
        canonical="https://www.invilic.com/blog/gst-invoice-format-india"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">India GST</span>
          </div>
          <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">India GST</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            GST Invoice Format Requirements India (2025)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 6 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            A GST invoice format under Indian law must include 13 mandatory fields specified in the CGST Rules 2017 — from the supplier's GSTIN and a consecutive serial number to a separate line for CGST/SGST or IGST. There is no prescribed template design, but every mandatory field must be present or the invoice is invalid.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Who Must Issue a GST Tax Invoice?</h2>
          <p className="mb-4">
            Every GST-registered supplier making a taxable supply of goods or services must issue a Tax Invoice. Registration is compulsory once aggregate annual turnover exceeds ₹40 lakh (goods) or ₹20 lakh (services), with a ₹10 lakh threshold in northeastern and special category states.
          </p>
          <p className="mb-6">
            Suppliers under the <strong className="font-semibold text-gray-900 dark:text-white">Composition Scheme</strong> do not issue Tax Invoices — they issue a Bill of Supply instead (see below). Suppliers of exempt goods or services also issue a Bill of Supply, not a Tax Invoice.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">All 13 Mandatory Fields — GST Tax Invoice</h2>
          <div className="space-y-3 mb-8">
            {mandatoryFields.map(({ field, rule }, i) => (
              <div key={field} className="flex gap-3 p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0 text-green-700 dark:text-green-300 font-bold text-xs">
                  {i + 1}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm mb-0.5">{field}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{rule}</p>
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Invilic generates GST-compliant tax invoices with all 13 mandatory fields — CGST/SGST/IGST auto-calculated. Free."
            to="/create/gst"
            btnText="Create GST Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Tax Invoice vs Bill of Supply</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="p-5 border border-green-100 dark:border-green-800/30 rounded-2xl bg-green-50 dark:bg-green-900/10">
              <p className="font-bold text-green-700 dark:text-green-300 mb-3 text-sm">Tax Invoice — Issue when:</p>
              <ul className="space-y-2 text-sm">
                {['Making any taxable supply of goods or services', 'Charging CGST + SGST or IGST', 'Buyer can use it to claim Input Tax Credit'].map(p => (
                  <li key={p} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />{p}</li>
                ))}
              </ul>
            </div>
            <div className="p-5 border border-blue-100 dark:border-blue-800/30 rounded-2xl bg-blue-50 dark:bg-blue-900/10">
              <p className="font-bold text-blue-700 dark:text-blue-300 mb-3 text-sm">Bill of Supply — Issue when:</p>
              <ul className="space-y-2 text-sm">
                {['Registered under Composition Scheme', 'Supplying exempt or nil-rated goods/services', 'No GST is charged (cannot charge GST on B/S)'].map(p => (
                  <li key={p} className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />{p}</li>
                ))}
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">E-Invoicing Requirements in 2025</h2>
          <p className="mb-4">
            E-invoicing (electronic invoicing through the GST Invoice Registration Portal) is mandatory for businesses with aggregate annual turnover above <strong className="font-semibold text-gray-900 dark:text-white">₹5 crore</strong>.
          </p>
          <p className="mb-4">
            Under e-invoicing, you must upload invoice data to the IRP (Invoice Registration Portal) before or at the time of supply. The IRP validates the data and returns an <strong className="font-semibold text-gray-900 dark:text-white">IRN (Invoice Reference Number)</strong> and a QR code, both of which must appear on the final invoice.
          </p>
          <p className="mb-6 text-sm p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl">
            <strong className="font-semibold text-gray-900 dark:text-white">Important:</strong> If you are below the ₹5 crore threshold, e-invoicing is not mandatory. You can continue issuing invoices through any compliant software and upload them to the GST portal through the standard returns process (GSTR-1).
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">HSN and SAC Code Requirements</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Annual Turnover</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">HSN Digits Required</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { turnover: 'Up to ₹5 crore', digits: '4-digit HSN code' },
                  { turnover: 'Above ₹5 crore', digits: '6-digit HSN code' },
                  { turnover: 'Services (all turnovers)', digits: '4-digit SAC code' },
                ].map(({ turnover, digits }) => (
                  <tr key={turnover} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800">{turnover}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{digits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a GST invoice with all mandatory fields — GSTIN, HSN/SAC, CGST/SGST/IGST, place of supply. Free, no account needed."
            to="/create/gst"
            btnText="Create GST Invoice"
          />

        </article>
        <RelatedArticles current="/blog/gst-invoice-format-india" />
      </main>
    </div>
  );
}
